import { useState, useCallback } from "react";
import { DownOutlined } from "@ant-design/icons";
import CustomSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import FeatureFlagGuard from "../../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDEBAR_ICONS } from "../../constants";
import { getToggleState } from "./utils";
import type { PermissionNode, PermissionState, ToggleState } from "../../types";
import styles from "./PermissionCollapse.module.css";

interface PermissionCollapseItemProps {
  node: PermissionNode;
  level: number;
  permissions: PermissionState;
  onToggle: (key: string, newValue: boolean) => void;
  disabled?: boolean;
  devOnlyKeys?: Set<string>;
}

function PermissionCollapseItem({
  node,
  level,
  permissions,
  onToggle,
  disabled = false,
  devOnlyKeys,
}: PermissionCollapseItemProps) {

  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(
    null,
  );
  const hasChildren = Boolean(node.items?.length);
  const toggleState: ToggleState = getToggleState(node, permissions);
  const isOn = toggleState === "on" || toggleState === "indeterminate";
  const isIndeterminate = toggleState === "indeterminate";
  const isMuted = toggleState === "off";

  // Only *partially* granted branches (`indeterminate`) expand by default.
  // Fully-granted (`on`) and fully-off (`off`) branches both stay collapsed:
  // the switch state already communicates them and the row is not actionable
  // beyond a single click. Users opt into either by clicking the row.
  const defaultExpanded = hasChildren && toggleState === "indeterminate";
  const isOpen = expandedOverride !== null ? expandedOverride : defaultExpanded;

  const toggleExpanded = useCallback(() => {
    if (!hasChildren) return;
    // Read from the *current effective* open state so the flip is against
    // what the user sees. Without this, the first click on a default-expanded
    // row would write `!false = true`, leaving the DOM unchanged.
    setExpandedOverride((prev) => {
      const currentlyOpen = prev !== null ? prev : defaultExpanded;
      return !currentlyOpen;
    });
  }, [hasChildren, defaultExpanded]);

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleExpanded();
    },
    [toggleExpanded],
  );

  const handleSwitchToggle = useCallback(() => {
    if (disabled) return;
    const newValue = toggleState === "off";
    onToggle(node.key, newValue);
  }, [node.key, toggleState, onToggle, disabled]);

  const levelClass =
    level === 0 ? styles.level0 : level === 1 ? styles.level1 : styles.level2;

  const icon = level === 0 ? SIDEBAR_ICONS[node.key.toLowerCase()] : undefined;

  return (
    <div className={styles.itemWrapper}>
      <div
        className={`${styles.row} ${levelClass} ${isMuted ? styles.muted : ""}`}
        onClick={handleToggleExpand}
        role={hasChildren ? "button" : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        onKeyDown={(e) => {
          if (hasChildren && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className={styles.rowLeft}>
          {icon && <img src={icon} alt="" className={styles.icon} />}
          <span className={styles.rowLabel}>{node.display_name}</span>
        </div>
        <div className={styles.rowRight}>
          {hasChildren && (
            <span
              className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
            >
              <DownOutlined />
            </span>
          )}
          <div
            className={styles.switchWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            <CustomSwitch
              eventCode={node.key}
              data={node}
              updateEventCode={handleSwitchToggle}
              status={isOn}
              disabled={disabled}
              sliderClassName={isIndeterminate ? styles.mixed : ""}
            />
          </div>
        </div>
      </div>

      {hasChildren && (
        <div
          className={`${styles.children} ${isOpen ? styles.childrenOpen : ""}`}
          data-testid={`children-${node.key}`}
          data-open={isOpen ? "true" : "false"}
        >
          {node.items!.map((child) => {
            const childItem = (
              <PermissionCollapseItem
                key={child.key}
                node={child}
                level={Math.min(level + 1, 2) as 0 | 1 | 2}
                permissions={permissions}
                onToggle={onToggle}
                disabled={disabled}
                devOnlyKeys={devOnlyKeys}
              />
            );

            if (devOnlyKeys?.has(child.key)) {
              return (
                <FeatureFlagGuard
                  key={child.key}
                  mode="conditional"
                  developmentMode
                >
                  {childItem}
                </FeatureFlagGuard>
              );
            }

            return childItem;
          })}
        </div>
      )}
    </div>
  );
}

export default PermissionCollapseItem;
